import * as Checkbox from '@radix-ui/react-checkbox';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowUpRight,
  Check,
  CheckCheck,
  CircleOff,
  Download,
  FileText,
  MessageSquareText,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import QueueDebugGraphView from '../components/QueueDebugGraphView';
import QueueTraceTimeline from '../components/QueueTraceTimeline';
import ResultView from '../components/screens/ResultView';
import {
  inputClassName,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../components/ui/ScreenPrimitives';
import {
  feedbackUrl,
  inputFileUrl,
  reviewAnswersUrl,
  userQueuesUrl,
} from '../lib/apiUrls';
import { withFirebaseAuthorization } from '../lib/authFetch';
import {
  completedStatuses,
  extractQueueDebugGraph,
  extractQueueDebugProgress,
  extractQueueDebugTrace,
  extractQueueExecutionNavigationTargets,
  extractQueueFailureMessage,
  extractQueueInputFile,
  extractQueueOutput,
  extractQueuePayload,
  extractReferenceDocumentName,
  extractReferenceDocumentUrl,
  formatFileSize,
  formatPercent,
  getInitialReviewResponses,
  getQueueScreenMode,
  getReviewQuestions,
  hasPendingRequiredReviewQuestions,
  isQuestionAnswered,
  normalizeProgress,
  normalizeQueue,
} from '../lib/appHelpers';

const primaryButtonClassName =
  'inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClassName =
  'inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt disabled:cursor-not-allowed disabled:opacity-60';
const dangerButtonClassName =
  'inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60';

function normalizeLoadedQueue(payload, queueId) {
  const queues = Array.isArray(payload?.queues) ? payload.queues : Array.isArray(payload) ? payload : [];
  return queues.find((queue) => queue?._id === queueId) || null;
}

function buildStage(queue, isLoading) {
  if (isLoading) {
    return { type: 'loading', message: 'Loading queue result...' };
  }

  if (!queue) {
    return { type: 'error', message: 'Queue result could not be loaded.' };
  }

  if (queue.status === 'failed') {
    return {
      type: 'error',
      message: extractQueueFailureMessage(queue) || 'Processing failed.',
    };
  }

  if (queue.status === 'needs_human_review') {
    return {
      type: 'warning',
      message: 'Processing is waiting for your review answers.',
    };
  }

  if (queue.status === 'completed') {
    return {
      type: 'success',
      message: 'Processing completed successfully.',
    };
  }

  const progress = normalizeProgress(extractQueueDebugProgress(queue));
  if (progress?.currentStage) {
    return {
      type: 'loading',
      message: `Currently ${progress.currentStage}.`,
    };
  }

  return {
    type: 'loading',
    message: 'Processing is still in progress.',
  };
}

function normalizeFeedback(queue) {
  return queue?.feedback || extractQueueOutput(queue)?.feedback || null;
}

function formatResolvedItemMeta(item) {
  return [
    item?.category ? `Category: ${item.category}` : null,
    item?.destination ? `Destination: ${item.destination}` : null,
    item?.confidence != null ? `Confidence: ${Math.round(item.confidence * 100)}%` : null,
    Array.isArray(item?.tags) && item.tags.length ? `Tags: ${item.tags.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' | ');
}

function toneForStatus(type) {
  if (type === 'warning') {
    return 'warning';
  }

  if (type === 'loading') {
    return 'loading';
  }

  if (type === 'success') {
    return 'success';
  }

  if (type === 'error') {
    return 'error';
  }

  return 'neutral';
}

function QueueResultPage({ auth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { queueId } = useParams();
  const [queue, setQueue] = useState(() => location.state?.queue || null);
  const [isLoadingQueue, setIsLoadingQueue] = useState(() => !location.state?.queue);
  const [queueLoadError, setQueueLoadError] = useState('');
  const [submittedInput, setSubmittedInput] = useState({
    status: 'idle',
    message: '',
    text: '',
  });
  const [referenceDocument, setReferenceDocument] = useState({
    status: 'idle',
    message: '',
    text: '',
  });
  const [reviewResponses, setReviewResponses] = useState(() =>
    getInitialReviewResponses(location.state?.queue || null)
  );
  const [reviewStatus, setReviewStatus] = useState({
    type: 'idle',
    message: '',
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [feedbackState, setFeedbackState] = useState(() => {
    const feedback = normalizeFeedback(location.state?.queue || null);
    return {
      outcome: feedback?.outcome || '',
      text: feedback?.feedbackText || '',
      status: { type: 'idle', message: '' },
      isSaving: false,
    };
  });

  useEffect(() => {
    setQueue(location.state?.queue || null);
  }, [location.state?.queue]);

  useEffect(() => {
    setReviewResponses(getInitialReviewResponses(queue));

    const feedback = normalizeFeedback(queue);
    setFeedbackState((current) => ({
      ...current,
      outcome: feedback?.outcome || '',
      text: feedback?.feedbackText || '',
    }));
  }, [queue]);

  useEffect(() => {
    if (queue || !queueId || !auth.currentUser?.userId) {
      setIsLoadingQueue(false);
      return;
    }

    let isMounted = true;

    async function loadQueue() {
      setIsLoadingQueue(true);
      setQueueLoadError('');

      try {
        const response = await fetch(userQueuesUrl(auth.currentUser.userId), await withFirebaseAuthorization());
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.error || 'Could not load queue history.');
        }

        const matchedQueue = normalizeLoadedQueue(payload, queueId);
        if (!matchedQueue) {
          throw new Error('That queue could not be found.');
        }

        if (isMounted) {
          setQueue(normalizeQueue(matchedQueue) || matchedQueue);
        }
      } catch (error) {
        if (isMounted) {
          setQueueLoadError(error.message || 'Could not load queue result.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingQueue(false);
        }
      }
    }

    void loadQueue();

    return () => {
      isMounted = false;
    };
  }, [auth.currentUser?.userId, queue, queueId]);

  const stage = useMemo(
    () => (queueLoadError ? { type: 'error', message: queueLoadError } : buildStage(queue, isLoadingQueue)),
    [isLoadingQueue, queue, queueLoadError]
  );

  const reviewQuestions = useMemo(() => getReviewQuestions(queue), [queue]);
  const allAnswered = useMemo(
    () =>
      reviewQuestions.every((question) =>
        question.optional ? true : isQuestionAnswered(question, reviewResponses[question.id])
      ),
    [reviewQuestions, reviewResponses]
  );

  const validateNavigation = () => {
    if (!queue || !hasPendingRequiredReviewQuestions(queue, reviewResponses)) {
      return true;
    }

    setReviewStatus({
      type: 'error',
      message: 'Answer or ignore every required review question before leaving this screen.',
    });
    return false;
  };

  const handleLoadSubmittedInput = async () => {
    if (!queue?._id) {
      return;
    }

    setSubmittedInput({
      status: 'loading',
      message: 'Loading submitted input...',
      text: '',
    });

    try {
      const response = await fetch(inputFileUrl(queue._id), await withFirebaseAuthorization());
      if (!response.ok) {
        throw new Error('Could not load submitted input.');
      }

      const text = await response.text();
      setSubmittedInput({
        status: 'success',
        message: 'Submitted input loaded.',
        text,
      });
    } catch (error) {
      setSubmittedInput({
        status: 'error',
        message: error.message || 'Could not load submitted input.',
        text: '',
      });
    }
  };

  const handleLoadReferenceDocument = async () => {
    const referenceUrl = extractReferenceDocumentUrl(queue);
    if (!referenceUrl) {
      return;
    }

    setReferenceDocument({
      status: 'loading',
      message: 'Loading reference document...',
      text: '',
    });

    try {
      const response = await fetch(referenceUrl);
      if (!response.ok) {
        throw new Error('Could not load the reference document.');
      }

      const text = await response.text();
      setReferenceDocument({
        status: 'success',
        message: 'Reference document loaded.',
        text,
      });
    } catch (error) {
      setReferenceDocument({
        status: 'error',
        message: error.message || 'Could not load the reference document.',
        text: '',
      });
    }
  };

  const handleReviewOptionToggle = (questionId, option, allowsMultiple) => {
    setReviewResponses((current) => {
      const existing = current[questionId] || {
        selectedOptions: [],
        answerText: '',
        ignored: false,
      };
      const alreadySelected = existing.selectedOptions.includes(option);
      const selectedOptions = allowsMultiple
        ? alreadySelected
          ? existing.selectedOptions.filter((value) => value !== option)
          : [...existing.selectedOptions, option]
        : [option];

      return {
        ...current,
        [questionId]: {
          ...existing,
          ignored: false,
          selectedOptions,
        },
      };
    });
  };

  const handleReviewTextChange = (questionId, answerText) => {
    setReviewResponses((current) => ({
      ...current,
      [questionId]: {
        ...(current[questionId] || {
          selectedOptions: [],
          ignored: false,
        }),
        answerText,
      },
    }));
  };

  const toggleIgnoredAnswer = (questionId) => {
    setReviewResponses((current) => {
      const existing = current[questionId] || {
        selectedOptions: [],
        answerText: '',
        ignored: false,
      };
      const nextIgnored = !existing.ignored;

      return {
        ...current,
        [questionId]: {
          ...existing,
          ignored: nextIgnored,
          selectedOptions: nextIgnored ? [] : existing.selectedOptions,
          answerText: nextIgnored ? 'Ignore' : '',
        },
      };
    });
  };

  const handleSubmitReviewAnswers = async () => {
    if (!queue?._id || !allAnswered) {
      return;
    }

    const answers = reviewQuestions
      .map((question) => {
        const response = reviewResponses[question.id];
        if (!response) {
          return null;
        }

        if (response.ignored) {
          return {
            questionId: question.id,
            answerText: 'Ignore',
          };
        }

        if (question.responseStyle === 'multiple_choice') {
          return {
            questionId: question.id,
            selectedOptions: response.selectedOptions || [],
          };
        }

        return {
          questionId: question.id,
          answerText: response.answerText || '',
        };
      })
      .filter(Boolean);

    setIsSubmittingReview(true);
    setReviewStatus({ type: 'loading', message: 'Submitting review answers...' });

    try {
      const response = await fetch(reviewAnswersUrl(queue._id), await withFirebaseAuthorization({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers }),
      }));
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Could not submit review answers.');
      }

      const nextQueue = normalizeQueue(payload) || payload?.queue || queue;
      setQueue(nextQueue);
      setReviewStatus({ type: 'success', message: 'Review answers submitted.' });
    } catch (error) {
      setReviewStatus({
        type: 'error',
        message: error.message || 'Could not submit review answers.',
      });
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const saveFeedback = async (outcome, feedbackText = '') => {
    if (!queue?._id) {
      return;
    }

    setFeedbackState((current) => ({
      ...current,
      isSaving: true,
      status: {
        type: 'loading',
        message: 'Saving feedback...',
      },
      outcome,
      text: feedbackText,
    }));

    try {
      const response = await fetch(feedbackUrl(queue._id), await withFirebaseAuthorization({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expectedOutcome: '',
          outcome,
          feedbackText,
        }),
      }));
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Could not save feedback.');
      }

      const nextQueue = normalizeQueue(payload) || payload?.queue || queue;
      setQueue(nextQueue);
      setFeedbackState((current) => ({
        ...current,
        isSaving: false,
        outcome,
        text: feedbackText,
        status: {
          type: 'success',
          message: 'Feedback saved.',
        },
      }));
    } catch (error) {
      setFeedbackState((current) => ({
        ...current,
        isSaving: false,
        status: {
          type: 'error',
          message: error.message || 'Could not save feedback.',
        },
      }));
    }
  };

  const renderSubmittedRequestSection = (currentQueue) => {
    const payload = extractQueuePayload(currentQueue);
    if (!payload) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Submitted request"
          copy="Original request context used to create this queue item."
        />
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {payload.scenarioName ? (
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Scenario
              </p>
              <p className="mt-2 text-sm font-medium text-app-ink">{payload.scenarioName}</p>
            </div>
          ) : null}
          {payload.scenarioId ? (
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Scenario ID
              </p>
              <p className="mt-2 break-all text-sm font-medium text-app-ink">{payload.scenarioId}</p>
            </div>
          ) : null}
          {payload.inputMode ? (
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Input mode
              </p>
              <p className="mt-2 text-sm font-medium text-app-ink">{payload.inputMode}</p>
            </div>
          ) : null}
        </div>
        {payload.message ? (
          <div className="mt-5 rounded-[22px] border border-app-border/70 bg-app-shell px-5 py-4">
            <p className="m-0 text-sm leading-7 text-app-ink">{payload.message}</p>
          </div>
        ) : null}
      </SectionCard>
    );
  };

  const renderInputSection = (currentQueue) => {
    const inputFile = extractQueueInputFile(currentQueue);
    const screenMode = getQueueScreenMode(currentQueue);

    if (!inputFile && screenMode === 'processing') {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Input"
          copy="Inspect the original file or text that created this queue item."
        />

        {inputFile ? (
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                File name
              </p>
              <p className="mt-2 text-sm font-medium text-app-ink">
                {inputFile.originalFileName || inputFile.fileName || 'Unknown'}
              </p>
            </div>
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Type
              </p>
              <p className="mt-2 text-sm font-medium text-app-ink">
                {inputFile.contentType || 'Unknown'}
              </p>
            </div>
            <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Size
              </p>
              <p className="mt-2 text-sm font-medium text-app-ink">
                {formatFileSize(inputFile.sizeBytes)}
              </p>
            </div>
          </div>
        ) : null}

        {currentQueue?._id ? (
          <div className="mt-5 flex flex-wrap gap-3">
            <button className={secondaryButtonClassName} type="button" onClick={handleLoadSubmittedInput}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              <span>Load submitted input</span>
            </button>
            <a
              className={secondaryButtonClassName}
              href={inputFileUrl(currentQueue._id, true)}
              target="_blank"
              rel="noreferrer"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              <span>Download input</span>
            </a>
          </div>
        ) : null}

        {submittedInput.message ? (
          <div className="mt-4">
            <StatusPill tone={toneForStatus(submittedInput.status)}>{submittedInput.message}</StatusPill>
          </div>
        ) : null}
        {submittedInput.text ? (
          <pre className="mt-5 overflow-x-auto rounded-[22px] border border-app-border/70 bg-app-shell p-5 text-sm leading-6 text-app-ink">
            {submittedInput.text}
          </pre>
        ) : null}
      </SectionCard>
    );
  };

  const renderCurrentProgressSection = (currentQueue) => {
    const progress = normalizeProgress(extractQueueDebugProgress(currentQueue));
    if (!progress) {
      return null;
    }

    return (
      <SectionCard className="bg-app-shell">
        <SectionTitle
          title="Current progress"
          copy="Latest reported pipeline stage and completion estimate."
          action={<StatusPill tone="accent">{formatPercent(progress.percent)}</StatusPill>}
        />
        <div className="mt-5 rounded-[22px] border border-app-border/70 bg-app-surface/95 p-5">
          <p className="m-0 text-lg font-semibold text-app-ink">
            {progress.currentStage || 'Processing'}
          </p>
        </div>
      </SectionCard>
    );
  };

  const renderProgressTimelineSection = (currentQueue) => {
    // Prefer the persisted trace stream (uncapped, sequence-ordered); fall back to the
    // capped progress.events mirror for older queue documents.
    const trace = extractQueueDebugTrace(currentQueue);
    const progress = normalizeProgress(extractQueueDebugProgress(currentQueue));
    const events = trace ?? progress?.events;
    if (!events?.length) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Trace timeline"
          copy="Chronological record of every stage, tool call, validation verdict, and execution outcome for this queue item."
        />
        <div className="mt-5">
          <QueueTraceTimeline events={events} />
        </div>
      </SectionCard>
    );
  };

  const renderOutputSection = (currentQueue) => {
    const output = extractQueueOutput(currentQueue);
    if (!output && currentQueue?.status !== 'failed') {
      return null;
    }

    const failureMessage = extractQueueFailureMessage(currentQueue);

    return (
      <SectionCard>
        <SectionTitle
          title={currentQueue?.status === 'failed' ? 'Failure details' : 'Output'}
          copy="Final response artifacts returned by the processing pipeline."
        />

        {failureMessage ? (
          <div className="mt-5">
            <StatusPill tone="error">{failureMessage}</StatusPill>
          </div>
        ) : null}

        <div className="mt-5 space-y-4">
          {output?.summary ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-shell px-5 py-4">
              <p className="m-0 text-sm leading-7 text-app-ink">{output.summary}</p>
            </div>
          ) : null}

          {output?.recommendedNextStep ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Recommended next step
              </p>
              <p className="mt-2 text-sm leading-7 text-app-ink">{output.recommendedNextStep}</p>
            </div>
          ) : null}

          {output?.resolutionSummary ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Resolution summary
              </p>
              <p className="mt-2 text-sm leading-7 text-app-ink">{output.resolutionSummary}</p>
            </div>
          ) : null}

          {output?.notes ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                Notes
              </p>
              <p className="mt-2 text-sm leading-7 text-app-ink">{output.notes}</p>
            </div>
          ) : null}

          {Array.isArray(output?.resolvedItems) && output.resolvedItems.length ? (
            <div>
              <p className="m-0 text-sm font-semibold text-app-ink">Resolved items</p>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                {output.resolvedItems.map((item) => (
                  <article
                    className="rounded-[22px] border border-app-border/80 bg-app-surface/95 p-4 shadow-subtle"
                    key={item.id || item.title}
                  >
                    <p className="m-0 text-sm font-semibold text-app-ink">
                      {item.title || 'Resolved item'}
                    </p>
                    {item.reason ? (
                      <p className="mt-2 text-sm leading-6 text-app-muted">{item.reason}</p>
                    ) : null}
                    {formatResolvedItemMeta(item) ? (
                      <p className="mt-3 text-xs leading-6 text-app-muted">
                        {formatResolvedItemMeta(item)}
                      </p>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {Array.isArray(output?.suggestions) && output.suggestions.length ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
              <p className="m-0 text-sm font-semibold text-app-ink">Suggestions</p>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-app-muted">
                {output.suggestions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {Array.isArray(output?.actions) && output.actions.length ? (
            <div className="rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
              <p className="m-0 text-sm font-semibold text-app-ink">Actions</p>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-app-muted">
                {output.actions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </SectionCard>
    );
  };

  const renderExecutionNavigationSection = (currentQueue) => {
    const executions = extractQueueExecutionNavigationTargets(currentQueue);
    if (!executions.length) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Navigation targets"
          copy="Open the workspace screens referenced by completed actions."
        />
        <div className="mt-5 flex flex-wrap gap-3">
          {executions.map((execution) => (
            <button
              className={secondaryButtonClassName}
              key={execution.id}
              type="button"
              onClick={() => navigate(`/${execution.navigationTarget.screen}`)}
            >
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              <span>{execution.label}</span>
            </button>
          ))}
        </div>
      </SectionCard>
    );
  };

  const renderDebugGraphSection = (currentQueue) => {
    const graph = extractQueueDebugGraph(currentQueue);
    if (!graph) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Debug graph"
          copy="Pipeline graph captured for this queue item."
          action={<StatusPill tone="accent">Available</StatusPill>}
        />
        <div className="mt-5 overflow-hidden rounded-[22px] border border-app-border/70 bg-app-shell">
          <QueueDebugGraphView graph={graph} />
        </div>
      </SectionCard>
    );
  };

  const renderReviewQuestionsSection = (currentQueue) => {
    if (currentQueue?.status !== 'needs_human_review' || !reviewQuestions.length) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Review questions"
          copy="Answer the remaining questions so processing can continue."
          action={<StatusPill tone="warning">{reviewQuestions.length} pending</StatusPill>}
        />

        <div className="mt-5 space-y-4">
          {reviewQuestions.map((question) => {
            const response = reviewResponses[question.id] || {
              selectedOptions: [],
              answerText: '',
              ignored: false,
            };

            return (
              <div
                className="rounded-[22px] border border-app-border/80 bg-app-surface/95 p-5 shadow-subtle"
                key={question.id}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-3xl">
                    <p className="m-0 text-sm font-semibold text-app-ink">{question.prompt}</p>
                    {question.reason ? (
                      <p className="mt-2 text-sm leading-6 text-app-muted">{question.reason}</p>
                    ) : null}
                  </div>
                  <StatusPill tone={question.optional ? 'neutral' : 'warning'}>
                    {question.optional ? 'Optional' : 'Required'}
                  </StatusPill>
                </div>

                {question.responseStyle === 'multiple_choice' ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {(question.options || []).map((option) => {
                      const checked = response.selectedOptions.includes(option);
                      return (
                        <label
                          className="flex cursor-pointer items-center gap-3 rounded-xl border border-app-border/70 bg-app-surface-alt/70 px-4 py-3 text-sm text-app-ink transition hover:bg-app-surface-alt"
                          key={option}
                        >
                          <Checkbox.Root
                            checked={checked}
                            onCheckedChange={() =>
                              handleReviewOptionToggle(question.id, option, question.allowsMultiple)
                            }
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-app-border bg-app-surface text-transparent data-[state=checked]:border-emerald-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:text-white"
                          >
                            <Checkbox.Indicator>
                              <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            </Checkbox.Indicator>
                          </Checkbox.Root>
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <textarea
                      rows="4"
                      value={response.ignored ? '' : response.answerText}
                      onChange={(event) => handleReviewTextChange(question.id, event.target.value)}
                      disabled={response.ignored}
                      placeholder="Type your answer here"
                      className={inputClassName}
                    />
                    <button
                      className={secondaryButtonClassName}
                      type="button"
                      onClick={() => toggleIgnoredAnswer(question.id)}
                    >
                      <CircleOff className="h-4 w-4" aria-hidden="true" />
                      <span>{response.ignored ? 'Use answer instead' : 'Ignore question'}</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {reviewStatus.message ? (
          <div className="mt-5">
            <StatusPill tone={toneForStatus(reviewStatus.type)}>{reviewStatus.message}</StatusPill>
          </div>
        ) : null}

        <div className="mt-5">
          <button
            className={primaryButtonClassName}
            type="button"
            onClick={handleSubmitReviewAnswers}
            disabled={!allAnswered || isSubmittingReview}
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            <span>{isSubmittingReview ? 'Submitting...' : 'Submit review answers'}</span>
          </button>
        </div>
      </SectionCard>
    );
  };

  const renderFeedbackSection = (currentQueue) => {
    if (!completedStatuses.has(currentQueue?.status)) {
      return null;
    }

    const existingFeedback = normalizeFeedback(currentQueue);
    const hasExistingFeedback = Boolean(
      existingFeedback?.outcome || existingFeedback?.feedbackText
    );
    const isThumbsDown =
      feedbackState.outcome === 'did_not_meet_expectation' || feedbackState.text;
    const saveLabel = hasExistingFeedback ? 'Update feedback' : 'Save feedback';

    return (
      <SectionCard>
        <SectionTitle
          title="Feedback"
          copy="Capture whether the result met expectations and what still needs work."
        />

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            className={
              feedbackState.outcome === 'met_expectation'
                ? primaryButtonClassName
                : secondaryButtonClassName
            }
            type="button"
            onClick={() => void saveFeedback('met_expectation', '')}
            disabled={feedbackState.isSaving}
          >
            <ThumbsUp className="h-4 w-4" aria-hidden="true" />
            <span>Thumbs up</span>
          </button>
          <button
            className={isThumbsDown ? dangerButtonClassName : secondaryButtonClassName}
            type="button"
            onClick={() =>
              setFeedbackState((current) => ({
                ...current,
                outcome: 'did_not_meet_expectation',
              }))
            }
            disabled={feedbackState.isSaving}
          >
            <ThumbsDown className="h-4 w-4" aria-hidden="true" />
            <span>Thumbs down</span>
          </button>
        </div>

        {isThumbsDown ? (
          <div className="mt-5 space-y-3">
            <label className="text-sm font-semibold text-app-ink" htmlFor="feedback-text">
              What missed the mark?
            </label>
            <textarea
              id="feedback-text"
              rows="4"
              value={feedbackState.text}
              onChange={(event) =>
                setFeedbackState((current) => ({
                  ...current,
                  text: event.target.value,
                  outcome: 'did_not_meet_expectation',
                }))
              }
              className={inputClassName}
            />
            <button
              className={primaryButtonClassName}
              type="button"
              onClick={() => void saveFeedback('did_not_meet_expectation', feedbackState.text)}
              disabled={feedbackState.isSaving}
            >
              <MessageSquareText className="h-4 w-4" aria-hidden="true" />
              <span>{feedbackState.isSaving ? 'Saving...' : saveLabel}</span>
            </button>
          </div>
        ) : null}

        {feedbackState.status.message ? (
          <div className="mt-5">
            <StatusPill tone={toneForStatus(feedbackState.status.type)}>
              {feedbackState.status.message}
            </StatusPill>
          </div>
        ) : null}
      </SectionCard>
    );
  };

  const renderReferenceDocumentSection = (currentQueue) => {
    const referenceUrl = extractReferenceDocumentUrl(currentQueue);
    if (!referenceUrl || !completedStatuses.has(currentQueue?.status)) {
      return null;
    }

    return (
      <SectionCard>
        <SectionTitle
          title="Reference document"
          copy="Review the original source document if you need more context."
        />
        <div className="mt-5 rounded-[22px] border border-app-border/70 bg-app-surface-alt/70 p-5">
          <p className="m-0 text-sm font-semibold text-app-ink">
            {extractReferenceDocumentName(currentQueue)}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={secondaryButtonClassName} type="button" onClick={handleLoadReferenceDocument}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              <span>Load reference document</span>
            </button>
            <a
              className={secondaryButtonClassName}
              href={referenceUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              <span>Open original</span>
            </a>
          </div>
        </div>

        {referenceDocument.message ? (
          <div className="mt-4">
            <StatusPill tone={toneForStatus(referenceDocument.status)}>
              {referenceDocument.message}
            </StatusPill>
          </div>
        ) : null}
        {referenceDocument.text ? (
          <pre className="mt-5 overflow-x-auto rounded-[22px] border border-app-border/70 bg-app-shell p-5 text-sm leading-6 text-app-ink">
            {referenceDocument.text}
          </pre>
        ) : null}
      </SectionCard>
    );
  };

  return (
    <ResultView
      queue={queue}
      lastProcessingJob={
        queue?.processingJobId ? { _id: queue.processingJobId, status: queue.status } : null
      }
      stage={stage}
      validateReviewBeforeNavigation={validateNavigation}
      renderSubmittedRequestSection={renderSubmittedRequestSection}
      renderInputSection={renderInputSection}
      renderCurrentProgressSection={renderCurrentProgressSection}
      renderProgressTimelineSection={renderProgressTimelineSection}
      renderOutputSection={renderOutputSection}
      renderExecutionNavigationSection={renderExecutionNavigationSection}
      renderDebugGraphSection={renderDebugGraphSection}
      renderReviewQuestionsSection={renderReviewQuestionsSection}
      renderFeedbackSection={renderFeedbackSection}
      renderReferenceDocumentSection={renderReferenceDocumentSection}
    />
  );
}

export default QueueResultPage;
